import { Button, Dropdown, Form, InputField, PageShell } from 'components';
import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatSwissAmount } from 'helpers';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tooltip } from 'primereact/tooltip';
import html2pdf from 'html2pdf.js';
import i18n from 'i18n';

class CalcSummary {
	grossAmount: number;
	payFlowFee: number;
	netAmount: number;
	socialDeductionEmployer: number;
	ahv: number;
	alv: number;
	fak: number;
	uvg: number;
	ktg: number;
	interimResult1: number;
	ahvAdmin: number;
	interimResult2: number;
	bvg: number;
	grossWage: number;

	constructor() {
		this.grossAmount = 0;
		this.payFlowFee = 0;
		this.netAmount = 0;
		this.socialDeductionEmployer = 0;
		this.ahv = 0;
		this.alv = 0;
		this.fak = 0;
		this.uvg = 0;
		this.ktg = 0;
		this.interimResult1 = 0;
		this.ahvAdmin = 0;
		this.interimResult2 = 0;
		this.bvg = 0;
		this.grossWage = 0;
	}
}

class EmployeeDeduction {
	ahv: number;
	alv: number;
	uvg: number;
	ktg: number;
	bvg: number;

	constructor() {
		this.ahv = 0;
		this.alv = 0;
		this.uvg = 0;
		this.ktg = 0;
		this.bvg = 0;
	}
}

const MARGIN_PERCENTAGE = 3;

export const SalaryCalculator = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const isPublicCalculator = location.pathname === '/calculator';

	useEffect(() => {
		if (!isPublicCalculator) return;

		document.documentElement.classList.add('calculator-embed');
		document.body.classList.add('calculator-embed');

		const script = document.createElement('script');
		script.src =
			'https://cdnjs.cloudflare.com/ajax/libs/iframe-resizer/4.2.11/iframeResizer.contentWindow.min.js';
		script.async = true;
		document.body.appendChild(script);

		return () => {
			document.documentElement.classList.remove('calculator-embed');
			document.body.classList.remove('calculator-embed');
			script.remove();
		};
	}, [isPublicCalculator]);

	const [formData, setFormData] = useState({
		simulationType: '',
		invoiceType: 'Hourly',
		hours: '1',
		timeRate: '',
		grossAmount: '',
		currency: 'CHF',
		age: '',
		serviceFee: '2',
		gavEnabled: true
	});
	const [rows, setRows] = useState<any>([]);
	const [showEmployerSocialBenefits, setShowEmployerSocialBenefits] = useState(true);
	const [showEmployeeSocialBenefits, setShowEmployeeSocialBenefits] = useState(true);
	const [totalEmployerSocialBenefits, setTotalEmployerSocialBenefits] = useState('');
	const [totalFullEmployerSocialBenefits, setTotalFullEmployerSocialBenefits] = useState('');
	const [totalEmployeeSocialBenefits, setTotalEmployeeSocialBenefits] = useState('');
	const [totalFullEmployeeSocialBenefits, setTotalFullEmployeeSocialBenefits] = useState('');
	const [showTable, setShowTable] = useState(false);
	const tableRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const language = searchParams.get('language');

		// Only override i18n when the calculator URL explicitly asks for a locale.
		// Otherwise keep the app language the user already selected.
		if (language === 'en' || language === 'de') {
			i18n.changeLanguage(language);
		}
	}, [location.search]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		const { name, value } = e.target;

		if (name === 'grossAmount' || name === 'age' || name === 'simulationType' || name === 'hours' || name === 'serviceFee') {
			setShowTable(false);
		}

		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleInvoiceTypeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		const { value } = e.target;
		setShowTable(false);
		const hoursMap: Record<string, string> = {
			'Hourly':    '1',
			'Daily':     '8',
			'Monthly':   '160',
			'Flat Rate': '180',
		};
		// Alles in einem einzigen setFormData — verhindert React-Batching-Probleme
		setFormData((prev) => ({
			...prev,
			invoiceType: value,
			hours: hoursMap[value] ?? prev.hours,
		}));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setShowEmployerSocialBenefits(true);
		setShowEmployeeSocialBenefits(true);
		const iType = formData.invoiceType;
		if (formData.simulationType === 'gross-wage' && formData.age !== '') {
			if (iType === 'Monthly')         calcGrossWageMonthly();
			else if (iType === 'Flat Rate')  calcGrossWageFlatRate();
			else                             calcGrossWage();
			setShowTable(true);
		}
		if (formData.simulationType === 'invoice-amount' && formData.age !== '') {
			if (iType === 'Monthly')         calcInvoiceAmountMonthly();
			else if (iType === 'Flat Rate')  calcInvoiceAmountFlatRate();
			else                             calcInvoiceAmount();
			setShowTable(true);
		}
	};

	const handleCancel = () => {
		setFormData({
			simulationType: '',
			invoiceType: '',
			hours: '',
			timeRate: '',
			grossAmount: '',
			currency: '',
			age: '',
			serviceFee: '2',
			gavEnabled: true
		});
		navigate('/');
	};

	const simulationOptions = [
		{ labelKey: 'salaryCalc.select', value: ''},
		{ labelKey: 'salaryCalc.invoiceAmountOption', value: 'invoice-amount' },
		{ labelKey: 'salaryCalc.grossWageOption', value: 'gross-wage' },
	];

	const contractOptions = [
		{ labelKey: 'typeOfInvoiceOptions.hourly', value: 'Hourly' },
		{ labelKey: 'typeOfInvoiceOptions.daily', value: 'Daily' },
		{ labelKey: 'typeOfInvoiceOptions.monthly', value: 'Monthly' },
		{ labelKey: 'salaryCalc.typeOfContractFlatRate', value: 'Flat Rate' },
	];

	const serviceFeeOptions = [
		{ labelKey: 'salaryCalc.serviceModelDiy', value: '2' },
		{ labelKey: 'salaryCalc.serviceModelFull', value: '3' },
	];

	const feeRate = Number(formData.serviceFee) / 100;
	const feeLabel = `PayFlow Service (${formData.serviceFee}%)`;

	const ageOptions = [
		{ labelKey: 'Select', value: ''},
		{ labelKey: '18 - 24', value: '18-24' },
		{ labelKey: '25 - 34', value: '25-34' },
		{ labelKey: '35 - 44', value: '35-44' },
		{ labelKey: '45 - 54', value: '45-54' },
		{ labelKey: '55 - 64', value: '55-64' },
	];

	// ─── Feste Sätze ────────────────────────────────────────────────────────────
	// Arbeitgeber
	const R_AHV_E     = 0.053;
	const R_ALV_E     = 0.011;
	const R_FAK       = 0.0135;
	const R_UVG_E     = 0.00073;
	const R_KTG_E     = 0.015;
	const R_AHV_ADMIN = R_AHV_E * 2 * 0.006; // 0.6% auf doppelten AHV

	// Arbeitnehmer
	const R_AHV_N = 0.053;
	const R_ALV_N = 0.011;
	const R_UVG_N = 0.0096;
	const R_KTG_N = 0.015;

	// BVG 50/50 — Arbeitgeber-Anteil = Gesamtsatz / 2
	const BVG_RATES: Record<string, number> = {
		'18-24': 0.026 / 2,
		'25-34': 0.096 / 2,
		'35-44': 0.126 / 2,
		'45-54': 0.176 / 2,
		'55-64': 0.206 / 2,
	};
	const BVG_MAX = 41.5;
	const BVG_MIN = 12.1;
	const CAP = 12350;
	const CAP_HOURLY = CAP / 160;

	// GAV Personalverleih — fest 0.4% AG / 0.4% AN
	const R_GAV_E  = 0.004;  // Arbeitgeber 0.4%
	const R_GAV_N  = 0.004;  // Arbeitnehmer 0.4%
	// Schwellenwert CHF/h: unter 50 = 75.80, ab 50 = 77.40
	const GAV_THRESHOLD_UNDER50 = 75.80;
	const GAV_THRESHOLD_OVER50  = 77.40;

	const getGavThreshold = (age: string): number => {
		// Altersgruppen ab 50: 45-54 (obere Hälfte), 55-64
		// Wir nutzen das Alter-Dropdown: 45-54 = Grenzbereich, 55-64 = ab 50
		// Für 45-54 braucht es eigentlich einen Over50-Toggle — hier nehmen wir unter50 als Default
		const over50 = age === '55-64';
		return over50 ? GAV_THRESHOLD_OVER50 : GAV_THRESHOLD_UNDER50;
	};

	const isGavActive = (gross: number, age: string, gavEnabled: boolean): boolean => {
		if (!gavEnabled) return false;
		return gross <= getGavThreshold(age);
	};

	// Monatlicher GAV-Schwellenwert: Stundenschwellenwert × 160
	const getGavThresholdMonthly = (age: string): number => {
		return getGavThreshold(age) * 160;
	};

	const isGavActiveMonthly = (grossMonth: number, age: string, gavEnabled: boolean): boolean => {
		if (!gavEnabled) return false;
		return grossMonth <= getGavThresholdMonthly(age);
	};

	const getBvgRate = (age: string): number => BVG_RATES[age] ?? 0;

	const calcBvg = (gross: number, age: string): number => {
		const rate = getBvgRate(age);
		if (!rate || gross <= BVG_MIN) return 0;
		const base = gross > BVG_MAX ? BVG_MAX - BVG_MIN : gross - BVG_MIN;
		return parseFloat((base * rate).toFixed(2));
	};

	const calcEmployerSocial = (gross: number, gavEnabled = false, age = '', cap = CAP_HOURLY, gavGross = gross) => {
		const capBase = Math.min(gross, cap);
		const ahv = gross * R_AHV_E;
		const alv = capBase * R_ALV_E;
		const fak = gross * R_FAK;
		const uvg = capBase * R_UVG_E;
		const ktg = gross * R_KTG_E;
		const ahvAdmin = ahv * 2 * 0.006;
		const gav = isGavActive(gavGross, age, gavEnabled) ? gross * R_GAV_E : 0;
		return { ahv, alv, fak, uvg, ktg, ahvAdmin, gav, total: ahv + alv + fak + uvg + ktg + ahvAdmin + gav };
	};

	const calcEmployeeSocial = (gross: number, gavEnabled = false, age = '', cap = CAP_HOURLY, gavGross = gross) => {
		const capBase = Math.min(gross, cap);
		const ahv = gross * R_AHV_N;
		const alv = capBase * R_ALV_N;
		const uvg = capBase * R_UVG_N;
		const ktg = gross * R_KTG_N;
		const gav = isGavActive(gavGross, age, gavEnabled) ? gross * R_GAV_N : 0;
		return { ahv, alv, uvg, ktg, gav, total: ahv + alv + uvg + ktg + gav };
	};

	// BVG monatlich: Koordinationsabzug 2'205, Maximum 7'560 CHF/Monat
	const BVG_MAX_M = 7560;
	const BVG_MIN_M = 2205;

	const calcBvgMonthly = (grossMonth: number, age: string): number => {
		const rate = getBvgRate(age);
		if (!rate || grossMonth <= BVG_MIN_M) return 0;
		const base = grossMonth >= BVG_MAX_M ? BVG_MAX_M - BVG_MIN_M : grossMonth - BVG_MIN_M;
		return parseFloat((base * rate).toFixed(2));
	};

	const calcEmployerSocialMonthly = (gross: number, gavEnabled = false, age = '') => {
		const capBase = Math.min(gross, CAP);
		const ahv = gross * R_AHV_E;
		const alv = capBase * R_ALV_E;
		const fak = gross * R_FAK;
		const uvg = capBase * R_UVG_E;
		const ktg = gross * R_KTG_E;
		const ahvAdmin = ahv * 2 * 0.006;
		const gav = isGavActiveMonthly(gross, age, gavEnabled) ? gross * R_GAV_E : 0;
		return { ahv, alv, fak, uvg, ktg, ahvAdmin, gav, total: ahv + alv + fak + uvg + ktg + ahvAdmin + gav };
	};

	const calcEmployeeSocialMonthly = (gross: number, gavEnabled = false, age = '') => {
		const capBase = Math.min(gross, CAP);
		const ahv = gross * R_AHV_N;
		const alv = capBase * R_ALV_N;
		const uvg = capBase * R_UVG_N;
		const ktg = gross * R_KTG_N;
		const gav = isGavActiveMonthly(gross, age, gavEnabled) ? gross * R_GAV_N : 0;
		return { ahv, alv, uvg, ktg, gav, total: ahv + alv + uvg + ktg + gav };
	};

	// Bei Monthly: immer eine Spalte (kein Stunden-Split)
	const hasHours = parseInt(formData.hours) > 1 && formData.invoiceType !== 'Monthly';

	/**
	 * Flat rate: All-In/h → (solver) Brutto-Periode, SV/BVG.
	 * Shared by Invoice-Amount and Gross-Wage (inverse) so round-trip is consistent.
	 */
	const flatRateAllInToDetails = (
		hourlyAllIn: number,
		hours: number,
		age: string,
		gav: boolean
	) => {
		const fee = parseFloat((hourlyAllIn * feeRate).toFixed(2));
		const svBasisTotal = parseFloat(((hourlyAllIn - fee) * hours).toFixed(2));

		let grossEst = svBasisTotal / 1.12;
		for (let i = 0; i < 30; i++) {
			const hourlyEst = grossEst / hours;
			const bvgEst = calcBvg(hourlyEst, age) * hours;
			const socEst = calcEmployerSocial(grossEst, gav, age, CAP, hourlyEst);
			const svCalc = grossEst + bvgEst + socEst.total;
			if (Math.abs(svCalc - svBasisTotal) < 0.001) break;
			grossEst = grossEst * (svBasisTotal / svCalc);
		}

		const periodGross = parseFloat(grossEst.toFixed(2));
		const hourlyGross = parseFloat((periodGross / hours).toFixed(4));
		const soc = calcEmployerSocial(periodGross, gav, age, CAP, hourlyGross);
		const bvg = parseFloat((calcBvg(hourlyGross, age) * hours).toFixed(2));
		const empSoc = calcEmployeeSocial(periodGross, gav, age, CAP, hourlyGross);
		const empBvg = bvg;
		const netWage = parseFloat((periodGross - empSoc.total - empBvg).toFixed(2));
		const gavApplied = isGavActive(hourlyGross, age, gav);
		const gavEAmt = parseFloat((soc.gav ?? 0).toFixed(2));
		const gavNAmt = parseFloat((empSoc.gav ?? 0).toFixed(2));
		return {
			hourlyAllIn,
			fee,
			periodGross,
			hourlyGross,
			soc,
			bvg,
			empSoc,
			empBvg,
			netWage,
			gavEAmt,
			gavNAmt,
			gavApplied,
		};
	};

	/**
	 * Inverts flatRateAllInToDetails for a target **displayed** gross/h (2 dp), so that
	 * re-using the value shown in "Invoice amount" (e.g. 88.50) recovers the same all-in
	 * (e.g. 100) and 170h total = 17'000.00, not 17'000.21 from Brutto-Periode rounding drift.
	 * Search in cent steps to keep output stable at 2 decimals.
	 */
	const findHourlyAllInForTargetGross2dp = (
		target2dp: number,
		hours: number,
		age: string,
		gav: boolean
	): number => {
		const target = Math.round(target2dp * 100) / 100;
		const g2Cent = (cents: number) =>
			parseFloat(flatRateAllInToDetails(cents / 100, hours, age, gav).hourlyGross.toFixed(2));

		let lo = 1;
		let hi = 100;
		while (g2Cent(hi) < target && hi < 100_000_000) {
			hi *= 2;
		}

		if (g2Cent(hi) < target) return hi / 100;

		// first cent with displayed gross >= target
		while (lo < hi) {
			const mid = Math.floor((lo + hi) / 2);
			if (g2Cent(mid) < target) lo = mid + 1;
			else hi = mid;
		}
		const first = lo;
		const firstVal = g2Cent(first);

		if (firstVal === target) {
			// choose the smallest matching cent value for deterministic round-trips
			return first / 100;
		}

		const prev = Math.max(1, first - 1);
		const prevVal = g2Cent(prev);
		const prevErr = Math.abs(prevVal - target);
		const firstErr = Math.abs(firstVal - target);
		return (prevErr <= firstErr ? prev : first) / 100;
	};

	// ─────────────────────────────────────────────────────────────────
	// All-In → Brutto (Iterativer Solver)
	// ─────────────────────────────────────────────────────────────────
	const calcInvoiceAmount = () => {
		setRows([]);
		const calcSum = new CalcSummary();
		const hours = parseInt(formData.hours) || 1;
		const age   = formData.age;

		let allIn = parseFloat(formData.grossAmount);
		if (hours > 1) allIn = allIn / hours;

		// Fee: 2% vom All-In abziehen → SV-Basis
		const fee = parseFloat((allIn * feeRate).toFixed(2));
		const svBasis = parseFloat((allIn - fee).toFixed(2));

		const gav = formData.gavEnabled;
		// Iterativer Solver: finde grossWage so dass grossWage + social + bvg = svBasis
		let grossEst = svBasis / 1.12;
		for (let i = 0; i < 20; i++) {
			const bvgEst    = calcBvg(grossEst, age);
			const socEst    = calcEmployerSocial(grossEst, gav, age);
			const svCalc    = grossEst + bvgEst + socEst.total;
			if (Math.abs(svCalc - svBasis) < 0.0001) break;
			grossEst = grossEst * (svBasis / svCalc);
		}

		const grossWage = parseFloat(grossEst.toFixed(2));
		const soc       = calcEmployerSocial(grossWage, gav, age);
		const bvg       = calcBvg(grossWage, age);
		const empSoc    = calcEmployeeSocial(grossWage, gav, age);
		const empBvg    = bvg; // 50/50
		const netWage   = parseFloat((grossWage - empSoc.total - empBvg).toFixed(2));

		const gavApplied = isGavActive(grossWage, age, gav);
		calcSum.grossAmount = allIn;
		calcSum.grossWage   = grossWage;
		calcSum.ahv         = parseFloat(soc.ahv.toFixed(2));
		calcSum.alv         = parseFloat(soc.alv.toFixed(2));
		calcSum.fak         = parseFloat(soc.fak.toFixed(2));
		calcSum.uvg         = parseFloat(soc.uvg.toFixed(2));
		calcSum.ktg         = parseFloat(soc.ktg.toFixed(2));
		calcSum.ahvAdmin    = parseFloat(soc.ahvAdmin.toFixed(2));
		calcSum.bvg         = bvg;
		calcSum.socialDeductionEmployer = parseFloat(soc.total.toFixed(2));
		const gavEAmt  = parseFloat((soc.gav ?? 0).toFixed(2));
		const gavNAmt  = parseFloat((empSoc.gav ?? 0).toFixed(2));

		const totalEmpDeduction = parseFloat(empSoc.total.toFixed(2));
		setTotalEmployerSocialBenefits(calcSum.socialDeductionEmployer.toFixed(2));
		setTotalEmployeeSocialBenefits(totalEmpDeduction.toFixed(2));
		setTotalFullEmployerSocialBenefits((calcSum.socialDeductionEmployer * hours).toFixed(2));
		setTotalFullEmployeeSocialBenefits((totalEmpDeduction * hours).toFixed(2));

		const bvgLabel = getBvgLabel(age);

		setRows([
			{ id: 1,  description: t('salaryCalc.invoiceAmountOption'), currency: formData.currency, value: formatSwissAmount(allIn.toFixed(2)), highlight: true, fullValue: formatSwissAmount((allIn * hours).toFixed(2)) },
			{ id: 2,  description: feeLabel, currency: formData.currency, value: fee.toFixed(2), fullValue: (fee * hours).toFixed(2) },
			{ id: 3,  description: t('salaryCalc.employerSocialDeduction'), value: calcSum.socialDeductionEmployer.toFixed(2), currency: formData.currency, fullValue: (calcSum.socialDeductionEmployer * hours).toFixed(2), noBorder: true },
			{ id: 4,  description: t('salaryCalc.ahv') + ' (5.3%)', value: calcSum.ahv.toFixed(2), currency: formData.currency, fullValue: (calcSum.ahv * hours).toFixed(2), hidden: true },
			{ id: 5,  description: t('salaryCalc.alv') + ' (1.1%)', value: calcSum.alv.toFixed(2), currency: formData.currency, fullValue: (calcSum.alv * hours).toFixed(2), hidden: true },
			{ id: 6,  description: t('salaryCalc.fak') + ' (1.35%)', value: calcSum.fak.toFixed(2), currency: formData.currency, fullValue: (calcSum.fak * hours).toFixed(2), hidden: true },
			{ id: 7,  description: t('salaryCalc.uvg') + ' (0.073%)', value: calcSum.uvg.toFixed(2), currency: formData.currency, fullValue: (calcSum.uvg * hours).toFixed(2), hidden: true },
			{ id: 8,  description: t('salaryCalc.ktg') + ' (1.5%)', value: calcSum.ktg.toFixed(2), currency: formData.currency, fullValue: (calcSum.ktg * hours).toFixed(2), hidden: true },
			{ id: 9,  description: t('salaryCalc.ahvAdmin') + ' (0.6%)', value: calcSum.ahvAdmin.toFixed(2), currency: formData.currency, fullValue: (calcSum.ahvAdmin * hours).toFixed(2), hidden: true },
			...(gavApplied ? [{ id: 91, description: 'GAV Personalverleih AG (0.4%)', value: gavEAmt.toFixed(2), currency: formData.currency, fullValue: (gavEAmt * hours).toFixed(2), hidden: true }] : []),
			{ id: 10, description: bvgLabel, currency: formData.currency, value: calcSum.bvg.toFixed(2), fullValue: (calcSum.bvg * hours).toFixed(2) },
			{ id: 11, description: t('salaryCalc.grossWage'), value: formatSwissAmount(grossWage.toFixed(2)), highlight: true, currency: formData.currency, fullValue: formatSwissAmount((grossWage * hours).toFixed(2)) },
			{ id: 12, description: t('salaryCalc.employeeSocialDeduction'), value: totalEmpDeduction.toFixed(2), currency: formData.currency, fullValue: (totalEmpDeduction * hours).toFixed(2), noBorder: true },
			{ id: 13, description: t('salaryCalc.ahv') + ' (5.3%)', value: parseFloat(empSoc.ahv.toFixed(2)).toFixed(2), currency: formData.currency, fullValue: (empSoc.ahv * hours).toFixed(2), hidden: true },
			{ id: 14, description: t('salaryCalc.alv') + ' (1.1%)', value: parseFloat(empSoc.alv.toFixed(2)).toFixed(2), currency: formData.currency, fullValue: (empSoc.alv * hours).toFixed(2), hidden: true },
			{ id: 15, description: t('salaryCalc.uvg') + ' (0.96%)', value: parseFloat(empSoc.uvg.toFixed(2)).toFixed(2), currency: formData.currency, fullValue: (empSoc.uvg * hours).toFixed(2), hidden: true },
			{ id: 16, description: t('salaryCalc.ktg') + ' (1.5%)', value: parseFloat(empSoc.ktg.toFixed(2)).toFixed(2), currency: formData.currency, fullValue: (empSoc.ktg * hours).toFixed(2), hidden: true },
			...(gavApplied ? [{ id: 161, description: 'GAV Personalverleih AN (0.4%)', value: gavNAmt.toFixed(2), currency: formData.currency, fullValue: (gavNAmt * hours).toFixed(2), hidden: true }] : []),
			{ id: 17, description: bvgLabel, currency: formData.currency, value: empBvg.toFixed(2), fullValue: (empBvg * hours).toFixed(2) },
			{ id: 18, description: t('salaryCalc.netAmount'), value: formatSwissAmount(netWage.toFixed(2)), highlight: true, currency: formData.currency, fullValue: formatSwissAmount((netWage * hours).toFixed(2)) },
		]);
	}

	// ─────────────────────────────────────────────────────────────────
	// Brutto → All-In
	// ─────────────────────────────────────────────────────────────────
	const calcGrossWage = () => {
		setRows([]);
		const calcSum = new CalcSummary();
		const hours = parseInt(formData.hours) || 1;
		const age   = formData.age;

		let grossWage = parseFloat(formData.grossAmount);
		if (hours > 1) grossWage = grossWage / hours;

		const gav      = formData.gavEnabled;
		const soc      = calcEmployerSocial(grossWage, gav, age);
		const bvg      = calcBvg(grossWage, age);
		const svBasisG = parseFloat((grossWage + soc.total + bvg).toFixed(2));
		const feeG     = parseFloat((svBasisG * feeRate / (1 - feeRate)).toFixed(2));
		const allIn    = parseFloat((svBasisG + feeG).toFixed(2));
		const gavApplied = isGavActive(grossWage, age, gav);

		const empSoc = calcEmployeeSocial(grossWage, gav, age);
		const empBvg = bvg; // 50/50
		const netWage = parseFloat((grossWage - empSoc.total - empBvg).toFixed(2));

		calcSum.grossWage   = grossWage;
		calcSum.grossAmount = allIn;
		calcSum.payFlowFee  = feeG;
		calcSum.ahv         = parseFloat(soc.ahv.toFixed(2));
		calcSum.alv         = parseFloat(soc.alv.toFixed(2));
		calcSum.fak         = parseFloat(soc.fak.toFixed(2));
		calcSum.uvg         = parseFloat(soc.uvg.toFixed(2));
		calcSum.ktg         = parseFloat(soc.ktg.toFixed(2));
		calcSum.ahvAdmin    = parseFloat(soc.ahvAdmin.toFixed(2));
		calcSum.bvg         = bvg;
		calcSum.socialDeductionEmployer = parseFloat(soc.total.toFixed(2));
		const gavEAmtG = parseFloat((soc.gav ?? 0).toFixed(2));
		const gavNAmtG = parseFloat((empSoc.gav ?? 0).toFixed(2));

		const totalEmpDeduction = parseFloat(empSoc.total.toFixed(2));
		setTotalEmployerSocialBenefits(calcSum.socialDeductionEmployer.toFixed(2));
		setTotalEmployeeSocialBenefits(totalEmpDeduction.toFixed(2));
		setTotalFullEmployerSocialBenefits((calcSum.socialDeductionEmployer * hours).toFixed(2));
		setTotalFullEmployeeSocialBenefits((totalEmpDeduction * hours).toFixed(2));

		const bvgLabel = getBvgLabel(age);

		setRows([
			{ id: 1,  description: t('salaryCalc.invoiceAmountOption'), currency: formData.currency, value: formatSwissAmount(allIn.toFixed(2)), highlight: true, fullValue: formatSwissAmount((allIn * hours).toFixed(2)) },
			{ id: 2,  description: feeLabel, currency: formData.currency, value: feeG.toFixed(2), fullValue: (feeG * hours).toFixed(2) },
			{ id: 3,  description: t('salaryCalc.employerSocialDeduction'), value: calcSum.socialDeductionEmployer.toFixed(2), currency: formData.currency, fullValue: (calcSum.socialDeductionEmployer * hours).toFixed(2), noBorder: true },
			{ id: 4,  description: t('salaryCalc.ahv') + ' (5.3%)', value: calcSum.ahv.toFixed(2), currency: formData.currency, fullValue: (calcSum.ahv * hours).toFixed(2), hidden: true },
			{ id: 5,  description: t('salaryCalc.alv') + ' (1.1%)', value: calcSum.alv.toFixed(2), currency: formData.currency, fullValue: (calcSum.alv * hours).toFixed(2), hidden: true },
			{ id: 6,  description: t('salaryCalc.fak') + ' (1.35%)', value: calcSum.fak.toFixed(2), currency: formData.currency, fullValue: (calcSum.fak * hours).toFixed(2), hidden: true },
			{ id: 7,  description: t('salaryCalc.uvg') + ' (0.073%)', value: calcSum.uvg.toFixed(2), currency: formData.currency, fullValue: (calcSum.uvg * hours).toFixed(2), hidden: true },
			{ id: 8,  description: t('salaryCalc.ktg') + ' (1.5%)', value: calcSum.ktg.toFixed(2), currency: formData.currency, fullValue: (calcSum.ktg * hours).toFixed(2), hidden: true },
			{ id: 9,  description: t('salaryCalc.ahvAdmin') + ' (0.6%)', value: calcSum.ahvAdmin.toFixed(2), currency: formData.currency, fullValue: (calcSum.ahvAdmin * hours).toFixed(2), hidden: true },
			...(gavApplied ? [{ id: 91, description: 'GAV Personalverleih AG (0.4%)', value: gavEAmtG.toFixed(2), currency: formData.currency, fullValue: (gavEAmtG * hours).toFixed(2), hidden: true }] : []),
			{ id: 10, description: bvgLabel, currency: formData.currency, value: calcSum.bvg.toFixed(2), fullValue: (calcSum.bvg * hours).toFixed(2) },
			{ id: 11, description: t('salaryCalc.grossWage'), value: formatSwissAmount(grossWage.toFixed(2)), highlight: true, currency: formData.currency, fullValue: formatSwissAmount((grossWage * hours).toFixed(2)) },
			{ id: 12, description: t('salaryCalc.employeeSocialDeduction'), value: totalEmpDeduction.toFixed(2), currency: formData.currency, fullValue: (totalEmpDeduction * hours).toFixed(2), noBorder: true },
			{ id: 13, description: t('salaryCalc.ahv') + ' (5.3%)', value: parseFloat(empSoc.ahv.toFixed(2)).toFixed(2), currency: formData.currency, fullValue: (empSoc.ahv * hours).toFixed(2), hidden: true },
			{ id: 14, description: t('salaryCalc.alv') + ' (1.1%)', value: parseFloat(empSoc.alv.toFixed(2)).toFixed(2), currency: formData.currency, fullValue: (empSoc.alv * hours).toFixed(2), hidden: true },
			{ id: 15, description: t('salaryCalc.uvg') + ' (0.96%)', value: parseFloat(empSoc.uvg.toFixed(2)).toFixed(2), currency: formData.currency, fullValue: (empSoc.uvg * hours).toFixed(2), hidden: true },
			{ id: 16, description: t('salaryCalc.ktg') + ' (1.5%)', value: parseFloat(empSoc.ktg.toFixed(2)).toFixed(2), currency: formData.currency, fullValue: (empSoc.ktg * hours).toFixed(2), hidden: true },
			...(gavApplied ? [{ id: 161, description: 'GAV Personalverleih AN (0.4%)', value: gavNAmtG.toFixed(2), currency: formData.currency, fullValue: (gavNAmtG * hours).toFixed(2), hidden: true }] : []),
			{ id: 17, description: bvgLabel, currency: formData.currency, value: empBvg.toFixed(2), fullValue: (empBvg * hours).toFixed(2) },
			{ id: 18, description: t('salaryCalc.netAmount'), value: formatSwissAmount(netWage.toFixed(2)), highlight: true, currency: formData.currency, fullValue: formatSwissAmount((netWage * hours).toFixed(2)) },
		]);
	}


	// ─────────────────────────────────────────────────────────────────
	// Monatslohn All-In → Brutto (iterativer Solver, monatliche BVG-Koordination)
	// ─────────────────────────────────────────────────────────────────
	const calcInvoiceAmountMonthly = () => {
		setRows([]);
		const age = formData.age;
		const gav = formData.gavEnabled;

		const allIn = parseFloat(formData.grossAmount);
		const fee   = parseFloat((allIn * feeRate).toFixed(2));
		const svBasis = parseFloat((allIn - fee).toFixed(2));

		// Iterativer Solver auf monatlichem Niveau
		let grossEst = svBasis / 1.12;
		for (let i = 0; i < 20; i++) {
			const bvgEst  = calcBvgMonthly(grossEst, age);
			const socEst  = calcEmployerSocialMonthly(grossEst, gav, age);
			const svCalc  = grossEst + bvgEst + socEst.total;
			if (Math.abs(svCalc - svBasis) < 0.001) break;
			grossEst = grossEst * (svBasis / svCalc);
		}

		const grossWage = parseFloat(grossEst.toFixed(2));
		const soc       = calcEmployerSocialMonthly(grossWage, gav, age);
		const bvg       = calcBvgMonthly(grossWage, age);
		const empSoc    = calcEmployeeSocialMonthly(grossWage, gav, age);
		const empBvg    = bvg;
		const netWage   = parseFloat((grossWage - empSoc.total - empBvg).toFixed(2));
		const gavApplied = isGavActiveMonthly(grossWage, age, gav);
		const gavEAmt   = parseFloat((soc.gav ?? 0).toFixed(2));
		const gavNAmt   = parseFloat((empSoc.gav ?? 0).toFixed(2));

		setTotalEmployerSocialBenefits(soc.total.toFixed(2));
		setTotalFullEmployerSocialBenefits(soc.total.toFixed(2));
		setTotalEmployeeSocialBenefits(empSoc.total.toFixed(2));
		setTotalFullEmployeeSocialBenefits(empSoc.total.toFixed(2));

		const bvgLabel = getBvgLabel(age);
		const cur = formData.currency;

		setRows([
			{ id: 1,   description: t('salaryCalc.invoiceAmountOption'), currency: cur, value: formatSwissAmount(allIn.toFixed(2)), highlight: true },
			{ id: 2,   description: feeLabel, currency: cur, value: fee.toFixed(2) },
			{ id: 3,   description: t('salaryCalc.employerSocialDeduction'), value: soc.total.toFixed(2), currency: cur, noBorder: true },
			{ id: 4,   description: t('salaryCalc.ahv') + ' (5.3%)', value: parseFloat(soc.ahv.toFixed(2)).toFixed(2), currency: cur, hidden: true },
			{ id: 5,   description: t('salaryCalc.alv') + ' (1.1%)', value: parseFloat(soc.alv.toFixed(2)).toFixed(2), currency: cur, hidden: true },
			{ id: 6,   description: t('salaryCalc.fak') + ' (1.35%)', value: parseFloat(soc.fak.toFixed(2)).toFixed(2), currency: cur, hidden: true },
			{ id: 7,   description: t('salaryCalc.uvg') + ' (0.073%)', value: parseFloat(soc.uvg.toFixed(2)).toFixed(2), currency: cur, hidden: true },
			{ id: 8,   description: t('salaryCalc.ktg') + ' (1.5%)', value: parseFloat(soc.ktg.toFixed(2)).toFixed(2), currency: cur, hidden: true },
			{ id: 9,   description: t('salaryCalc.ahvAdmin') + ' (0.6%)', value: parseFloat(soc.ahvAdmin.toFixed(2)).toFixed(2), currency: cur, hidden: true },
			...(gavApplied ? [{ id: 91, description: 'GAV Personalverleih AG (0.4%)', value: gavEAmt.toFixed(2), currency: cur, hidden: true }] : []),
			{ id: 10,  description: bvgLabel, currency: cur, value: bvg.toFixed(2) },
			{ id: 11,  description: t('salaryCalc.grossWage'), value: formatSwissAmount(grossWage.toFixed(2)), highlight: true, currency: cur },
			{ id: 12,  description: t('salaryCalc.employeeSocialDeduction'), value: empSoc.total.toFixed(2), currency: cur, noBorder: true },
			{ id: 13,  description: t('salaryCalc.ahv') + ' (5.3%)', value: parseFloat(empSoc.ahv.toFixed(2)).toFixed(2), currency: cur, hidden: true },
			{ id: 14,  description: t('salaryCalc.alv') + ' (1.1%)', value: parseFloat(empSoc.alv.toFixed(2)).toFixed(2), currency: cur, hidden: true },
			{ id: 15,  description: t('salaryCalc.uvg') + ' (0.96%)', value: parseFloat(empSoc.uvg.toFixed(2)).toFixed(2), currency: cur, hidden: true },
			{ id: 16,  description: t('salaryCalc.ktg') + ' (1.5%)', value: parseFloat(empSoc.ktg.toFixed(2)).toFixed(2), currency: cur, hidden: true },
			...(gavApplied ? [{ id: 161, description: 'GAV Personalverleih AN (0.4%)', value: gavNAmt.toFixed(2), currency: cur, hidden: true }] : []),
			{ id: 17,  description: bvgLabel, currency: cur, value: empBvg.toFixed(2) },
			{ id: 18,  description: t('salaryCalc.netAmount'), value: formatSwissAmount(netWage.toFixed(2)), highlight: true, currency: cur },
		]);
	};

	// ─────────────────────────────────────────────────────────────────
	// Monatslohn Brutto → All-In
	// ─────────────────────────────────────────────────────────────────
	const calcGrossWageMonthly = () => {
		setRows([]);
		const age = formData.age;
		const gav = formData.gavEnabled;

		const grossWage = parseFloat(formData.grossAmount);
		const soc       = calcEmployerSocialMonthly(grossWage, gav, age);
		const bvg       = calcBvgMonthly(grossWage, age);
		const svBasisG  = parseFloat((grossWage + soc.total + bvg).toFixed(2));
		const feeG      = parseFloat((svBasisG * feeRate / (1 - feeRate)).toFixed(2));
		const allIn     = parseFloat((svBasisG + feeG).toFixed(2));
		const empSoc    = calcEmployeeSocialMonthly(grossWage, gav, age);
		const empBvg    = bvg;
		const netWage   = parseFloat((grossWage - empSoc.total - empBvg).toFixed(2));
		const gavApplied = isGavActiveMonthly(grossWage, age, gav);
		const gavEAmt   = parseFloat((soc.gav ?? 0).toFixed(2));
		const gavNAmt   = parseFloat((empSoc.gav ?? 0).toFixed(2));

		setTotalEmployerSocialBenefits(soc.total.toFixed(2));
		setTotalFullEmployerSocialBenefits(soc.total.toFixed(2));
		setTotalEmployeeSocialBenefits(empSoc.total.toFixed(2));
		setTotalFullEmployeeSocialBenefits(empSoc.total.toFixed(2));

		const bvgLabel = getBvgLabel(age);
		const cur = formData.currency;

		setRows([
			{ id: 1,   description: t('salaryCalc.invoiceAmountOption'), currency: cur, value: formatSwissAmount(allIn.toFixed(2)), highlight: true },
			{ id: 2,   description: feeLabel, currency: cur, value: feeG.toFixed(2) },
			{ id: 3,   description: t('salaryCalc.employerSocialDeduction'), value: soc.total.toFixed(2), currency: cur, noBorder: true },
			{ id: 4,   description: t('salaryCalc.ahv') + ' (5.3%)', value: parseFloat(soc.ahv.toFixed(2)).toFixed(2), currency: cur, hidden: true },
			{ id: 5,   description: t('salaryCalc.alv') + ' (1.1%)', value: parseFloat(soc.alv.toFixed(2)).toFixed(2), currency: cur, hidden: true },
			{ id: 6,   description: t('salaryCalc.fak') + ' (1.35%)', value: parseFloat(soc.fak.toFixed(2)).toFixed(2), currency: cur, hidden: true },
			{ id: 7,   description: t('salaryCalc.uvg') + ' (0.073%)', value: parseFloat(soc.uvg.toFixed(2)).toFixed(2), currency: cur, hidden: true },
			{ id: 8,   description: t('salaryCalc.ktg') + ' (1.5%)', value: parseFloat(soc.ktg.toFixed(2)).toFixed(2), currency: cur, hidden: true },
			{ id: 9,   description: t('salaryCalc.ahvAdmin') + ' (0.6%)', value: parseFloat(soc.ahvAdmin.toFixed(2)).toFixed(2), currency: cur, hidden: true },
			...(gavApplied ? [{ id: 91, description: 'GAV Personalverleih AG (0.4%)', value: gavEAmt.toFixed(2), currency: cur, hidden: true }] : []),
			{ id: 10,  description: bvgLabel, currency: cur, value: bvg.toFixed(2) },
			{ id: 11,  description: t('salaryCalc.grossWage'), value: formatSwissAmount(grossWage.toFixed(2)), highlight: true, currency: cur },
			{ id: 12,  description: t('salaryCalc.employeeSocialDeduction'), value: empSoc.total.toFixed(2), currency: cur, noBorder: true },
			{ id: 13,  description: t('salaryCalc.ahv') + ' (5.3%)', value: parseFloat(empSoc.ahv.toFixed(2)).toFixed(2), currency: cur, hidden: true },
			{ id: 14,  description: t('salaryCalc.alv') + ' (1.1%)', value: parseFloat(empSoc.alv.toFixed(2)).toFixed(2), currency: cur, hidden: true },
			{ id: 15,  description: t('salaryCalc.uvg') + ' (0.96%)', value: parseFloat(empSoc.uvg.toFixed(2)).toFixed(2), currency: cur, hidden: true },
			{ id: 16,  description: t('salaryCalc.ktg') + ' (1.5%)', value: parseFloat(empSoc.ktg.toFixed(2)).toFixed(2), currency: cur, hidden: true },
			...(gavApplied ? [{ id: 161, description: 'GAV Personalverleih AN (0.4%)', value: gavNAmt.toFixed(2), currency: cur, hidden: true }] : []),
			{ id: 17,  description: bvgLabel, currency: cur, value: empBvg.toFixed(2) },
			{ id: 18,  description: t('salaryCalc.netAmount'), value: formatSwissAmount(netWage.toFixed(2)), highlight: true, currency: cur },
		]);
	};

	// ─────────────────────────────────────────────────────────────────
	// Flat Rate All-In → Brutto
	// Eingabe: Stundensatz All-In, Stunden
	// Solver auf Stundenbasis (wie Individual im Angular-Code)
	// SV auf Periodenlohn (hours × hourlyGross), BVG stündlich × hours
	// ─────────────────────────────────────────────────────────────────
	const calcInvoiceAmountFlatRate = () => {
		setRows([]);
		const age   = formData.age;
		const gav   = formData.gavEnabled;
		const hours = parseInt(formData.hours) || 1;

		const hourlyAllIn  = parseFloat(formData.grossAmount);
		const d = flatRateAllInToDetails(hourlyAllIn, hours, age, gav);
		const { periodGross, hourlyGross, soc, bvg, empSoc, netWage, gavEAmt, gavNAmt, gavApplied, fee } = d;
		const empBvg = bvg;

		setTotalEmployerSocialBenefits((soc.total / hours).toFixed(2));
		setTotalFullEmployerSocialBenefits(soc.total.toFixed(2));
		setTotalEmployeeSocialBenefits((empSoc.total / hours).toFixed(2));
		setTotalFullEmployeeSocialBenefits(empSoc.total.toFixed(2));

		const bvgLabel = getBvgLabel(age);
		const cur = formData.currency;

		setRows([
			{ id: 1,   description: t('salaryCalc.invoiceAmountOption'), currency: cur, value: formatSwissAmount(hourlyAllIn.toFixed(2)), highlight: true, fullValue: formatSwissAmount((hourlyAllIn * hours).toFixed(2)) },
			{ id: 2,   description: feeLabel, currency: cur, value: fee.toFixed(2), fullValue: (fee * hours).toFixed(2) },
			{ id: 3,   description: t('salaryCalc.employerSocialDeduction'), value: (soc.total / hours).toFixed(2), currency: cur, fullValue: soc.total.toFixed(2), noBorder: true },
			{ id: 4,   description: t('salaryCalc.ahv') + ' (5.3%)', value: (soc.ahv / hours).toFixed(2), currency: cur, fullValue: soc.ahv.toFixed(2), hidden: true },
			{ id: 5,   description: t('salaryCalc.alv') + ' (1.1%)', value: (soc.alv / hours).toFixed(2), currency: cur, fullValue: soc.alv.toFixed(2), hidden: true },
			{ id: 6,   description: t('salaryCalc.fak') + ' (1.35%)', value: (soc.fak / hours).toFixed(2), currency: cur, fullValue: soc.fak.toFixed(2), hidden: true },
			{ id: 7,   description: t('salaryCalc.uvg') + ' (0.073%)', value: (soc.uvg / hours).toFixed(2), currency: cur, fullValue: soc.uvg.toFixed(2), hidden: true },
			{ id: 8,   description: t('salaryCalc.ktg') + ' (1.5%)', value: (soc.ktg / hours).toFixed(2), currency: cur, fullValue: soc.ktg.toFixed(2), hidden: true },
			{ id: 9,   description: t('salaryCalc.ahvAdmin') + ' (0.6%)', value: (soc.ahvAdmin / hours).toFixed(2), currency: cur, fullValue: soc.ahvAdmin.toFixed(2), hidden: true },
			...(gavApplied ? [{ id: 91, description: 'GAV Personalverleih AG (0.4%)', value: (gavEAmt / hours).toFixed(2), currency: cur, fullValue: gavEAmt.toFixed(2), hidden: true }] : []),
			{ id: 10,  description: bvgLabel, currency: cur, value: parseFloat(calcBvg(hourlyGross, age).toFixed(2)).toFixed(2), fullValue: bvg.toFixed(2) },
			{ id: 11,  description: t('salaryCalc.grossWage'), value: formatSwissAmount(parseFloat(hourlyGross.toFixed(2)).toFixed(2)), highlight: true, currency: cur, fullValue: formatSwissAmount(periodGross.toFixed(2)) },
			{ id: 12,  description: t('salaryCalc.employeeSocialDeduction'), value: (empSoc.total / hours).toFixed(2), currency: cur, fullValue: empSoc.total.toFixed(2), noBorder: true },
			{ id: 13,  description: t('salaryCalc.ahv') + ' (5.3%)', value: (empSoc.ahv / hours).toFixed(2), currency: cur, fullValue: empSoc.ahv.toFixed(2), hidden: true },
			{ id: 14,  description: t('salaryCalc.alv') + ' (1.1%)', value: (empSoc.alv / hours).toFixed(2), currency: cur, fullValue: empSoc.alv.toFixed(2), hidden: true },
			{ id: 15,  description: t('salaryCalc.uvg') + ' (0.96%)', value: (empSoc.uvg / hours).toFixed(2), currency: cur, fullValue: empSoc.uvg.toFixed(2), hidden: true },
			{ id: 16,  description: t('salaryCalc.ktg') + ' (1.5%)', value: (empSoc.ktg / hours).toFixed(2), currency: cur, fullValue: empSoc.ktg.toFixed(2), hidden: true },
			...(gavApplied ? [{ id: 161, description: 'GAV Personalverleih AN (0.4%)', value: (gavNAmt / hours).toFixed(2), currency: cur, fullValue: gavNAmt.toFixed(2), hidden: true }] : []),
			{ id: 17,  description: bvgLabel, currency: cur, value: parseFloat(calcBvg(hourlyGross, age).toFixed(2)).toFixed(2), fullValue: bvg.toFixed(2) },
			{ id: 18,  description: t('salaryCalc.netAmount'), value: formatSwissAmount((netWage / hours).toFixed(2)), highlight: true, currency: cur, fullValue: formatSwissAmount(netWage.toFixed(2)) },
		]);
	};

	// ─────────────────────────────────────────────────────────────────
	// Flat Rate Brutto → All-In
	// Eingabe: Stundensatz Brutto; SV auf Periodenlohn, BVG stündlich × hours
	// ─────────────────────────────────────────────────────────────────
	const calcGrossWageFlatRate = () => {
		setRows([]);
		const age   = formData.age;
		const gav   = formData.gavEnabled;
		const hours = parseInt(formData.hours) || 1;

		const targetHourlyGross = parseFloat(formData.grossAmount);
		const matchedHourlyAllIn = findHourlyAllInForTargetGross2dp(
			isNaN(targetHourlyGross) ? 0 : targetHourlyGross,
			hours,
			age,
			gav
		);
		const d = flatRateAllInToDetails(matchedHourlyAllIn, hours, age, gav);
		const { periodGross, hourlyGross, soc, bvg, empSoc, netWage, gavEAmt, gavNAmt, gavApplied } = d;
		const empBvg = bvg;
		const allIn = d.hourlyAllIn * hours;
		const feeG = d.fee * hours;

		setTotalEmployerSocialBenefits((soc.total / hours).toFixed(2));
		setTotalFullEmployerSocialBenefits(soc.total.toFixed(2));
		setTotalEmployeeSocialBenefits((empSoc.total / hours).toFixed(2));
		setTotalFullEmployeeSocialBenefits(empSoc.total.toFixed(2));

		const bvgLabel = getBvgLabel(age);
		const cur = formData.currency;
		const hourlyAllIn = parseFloat((d.hourlyAllIn).toFixed(2));

		setRows([
			{ id: 1,   description: t('salaryCalc.invoiceAmountOption'), currency: cur, value: formatSwissAmount(hourlyAllIn.toFixed(2)), highlight: true, fullValue: formatSwissAmount(allIn.toFixed(2)) },
			{ id: 2,   description: feeLabel, currency: cur, value: (feeG / hours).toFixed(2), fullValue: feeG.toFixed(2) },
			{ id: 3,   description: t('salaryCalc.employerSocialDeduction'), value: (soc.total / hours).toFixed(2), currency: cur, fullValue: soc.total.toFixed(2), noBorder: true },
			{ id: 4,   description: t('salaryCalc.ahv') + ' (5.3%)', value: (soc.ahv / hours).toFixed(2), currency: cur, fullValue: soc.ahv.toFixed(2), hidden: true },
			{ id: 5,   description: t('salaryCalc.alv') + ' (1.1%)', value: (soc.alv / hours).toFixed(2), currency: cur, fullValue: soc.alv.toFixed(2), hidden: true },
			{ id: 6,   description: t('salaryCalc.fak') + ' (1.35%)', value: (soc.fak / hours).toFixed(2), currency: cur, fullValue: soc.fak.toFixed(2), hidden: true },
			{ id: 7,   description: t('salaryCalc.uvg') + ' (0.073%)', value: (soc.uvg / hours).toFixed(2), currency: cur, fullValue: soc.uvg.toFixed(2), hidden: true },
			{ id: 8,   description: t('salaryCalc.ktg') + ' (1.5%)', value: (soc.ktg / hours).toFixed(2), currency: cur, fullValue: soc.ktg.toFixed(2), hidden: true },
			{ id: 9,   description: t('salaryCalc.ahvAdmin') + ' (0.6%)', value: (soc.ahvAdmin / hours).toFixed(2), currency: cur, fullValue: soc.ahvAdmin.toFixed(2), hidden: true },
			...(gavApplied ? [{ id: 91, description: 'GAV Personalverleih AG (0.4%)', value: (gavEAmt / hours).toFixed(2), currency: cur, fullValue: gavEAmt.toFixed(2), hidden: true }] : []),
			{ id: 10,  description: bvgLabel, currency: cur, value: parseFloat(calcBvg(hourlyGross, age).toFixed(2)).toFixed(2), fullValue: bvg.toFixed(2) },
			{ id: 11,  description: t('salaryCalc.grossWage'), value: formatSwissAmount(hourlyGross.toFixed(2)), highlight: true, currency: cur, fullValue: formatSwissAmount(periodGross.toFixed(2)) },
			{ id: 12,  description: t('salaryCalc.employeeSocialDeduction'), value: (empSoc.total / hours).toFixed(2), currency: cur, fullValue: empSoc.total.toFixed(2), noBorder: true },
			{ id: 13,  description: t('salaryCalc.ahv') + ' (5.3%)', value: (empSoc.ahv / hours).toFixed(2), currency: cur, fullValue: empSoc.ahv.toFixed(2), hidden: true },
			{ id: 14,  description: t('salaryCalc.alv') + ' (1.1%)', value: (empSoc.alv / hours).toFixed(2), currency: cur, fullValue: empSoc.alv.toFixed(2), hidden: true },
			{ id: 15,  description: t('salaryCalc.uvg') + ' (0.96%)', value: (empSoc.uvg / hours).toFixed(2), currency: cur, fullValue: empSoc.uvg.toFixed(2), hidden: true },
			{ id: 16,  description: t('salaryCalc.ktg') + ' (1.5%)', value: (empSoc.ktg / hours).toFixed(2), currency: cur, fullValue: empSoc.ktg.toFixed(2), hidden: true },
			...(gavApplied ? [{ id: 161, description: 'GAV Personalverleih AN (0.4%)', value: (gavNAmt / hours).toFixed(2), currency: cur, fullValue: gavNAmt.toFixed(2), hidden: true }] : []),
			{ id: 17,  description: bvgLabel, currency: cur, value: parseFloat(calcBvg(hourlyGross, age).toFixed(2)).toFixed(2), fullValue: bvg.toFixed(2) },
			{ id: 18,  description: t('salaryCalc.netAmount'), value: formatSwissAmount((netWage / hours).toFixed(2)), highlight: true, currency: cur, fullValue: formatSwissAmount(netWage.toFixed(2)) },
		]);
	};

	const getAgeLabel = (age: string): string => {
		const map: Record<string, string> = {
			'18-24': '18 - 24',
			'25-34': '25 - 34',
			'35-44': '35 - 44',
			'45-54': '45 - 54',
			'55-64': '55 - 64',
		};
		return map[age] ?? '';
	}

	const getBvgLabel = (age: string): string => {
		// Zeigt AG-Anteil (50% des Gesamtsatzes)
		const halfPct: Record<string, string> = {
			'18-24': '1.3%',
			'25-34': '4.8%',
			'35-44': '6.3%',
			'45-54': '8.8%',
			'55-64': '10.3%',
		};
		const pct = halfPct[age] ?? '';
		return `BVG (50%) ${getAgeLabel(age)}${pct ? ' ' + pct : ''}`;
	}

	const getTypeOfSimulationLabel = (value: string) => {
		switch (value) {
			case 'gross-wage':
				return t('salaryCalc.grossWageOption');
			case 'invoice-amount':
				return t('salaryCalc.invoiceAmountOption');
			default:
				return '';
		}
	}

	const getLabelGrossAmountByType = () => {
		switch (formData.invoiceType) {
			case 'Hourly':
				return getTypeOfSimulationLabel(formData.simulationType) + t('salaryCalc.invoiceAmountPerHour');
			case 'Daily':
				return getTypeOfSimulationLabel(formData.simulationType) + t('salaryCalc.invoiceAmountPerDay');
			case 'Monthly':
				return getTypeOfSimulationLabel(formData.simulationType) + t('salaryCalc.invoiceAmountPerMonth');
			case 'Flat Rate':
				return getTypeOfSimulationLabel(formData.simulationType) + t('salaryCalc.invoiceAmountFlatRate');
			default:
				return '';
		}
	}

	const generatePDF = (e?: any) => {
		e?.preventDefault();

		// Here need to modify rows to make hidden rows visible
		setShowEmployerSocialBenefits(false);
		setShowEmployeeSocialBenefits(false);
		const updatedRows = rows.map((row: any) => {
			if (row.hidden) {
				return { ...row, hidden: false };
			}
			if (row.id === 3) {
				return { ...row, value: !showEmployerSocialBenefits ? totalEmployerSocialBenefits : '', currency: !showEmployerSocialBenefits ? formData.currency : '', fullValue: !showEmployerSocialBenefits ? totalFullEmployerSocialBenefits : '' };
			}
			if (row.id === 12) {
				return { ...row, value: !showEmployeeSocialBenefits ? totalEmployeeSocialBenefits : '', currency: !showEmployeeSocialBenefits ? formData.currency : '', fullValue: !showEmployeeSocialBenefits ? totalFullEmployeeSocialBenefits : '' };
			}
			return row;
		});
		setRows(updatedRows);

		// Create a temporary div for PDF generation
		const tempDiv = document.createElement('div');
		tempDiv.innerHTML = `
			<div style="width: 100%; position: relative;">
				<div style="position: absolute; right: 40px; top: 10px;">
					<img src="/payflow-blue.svg" width="150" alt="PayFlow Logo" />
				</div>
			</div>
			<div style="height: 60px;"></div>
			<div class="custom-data-table">
				<table style="margin: 0 auto; width: 35rem; border-collapse: collapse;">
					<thead>
						<tr>
							<th style="background-color: #2f435f; color: #ffffff; font-size: 18px; text-align: left; padding-bottom: 16px;">${t('salaryCalc.totalCosts')}</th>
							<th style="background-color: #2f435f; color: #ffffff;"></th>
							<th style="background-color: #2f435f; color: #ffffff; text-align: center;">${hasHours ? '1h' : ''}</th>
							${hasHours ? `<th style="background-color: #2f435f; color: #ffffff; text-align: center;">${formData.hours}h</th>` : ''}
						</tr>
					</thead>
					<tbody>
						${updatedRows.map((row: { 
							description: string;
							currency?: string;
							value?: string;
							fullValue?: string;
							highlight?: boolean;
						}) => `
							<tr style="border-bottom: 1px solid #d3d3d3; height: 40px!important; ${row.highlight ? 'background-color: #2f435f; color: #ffffff; font-weight: 600;' : ''} ${row.description === t('salaryCalc.employerSocialDeduction') || row.description === t('salaryCalc.employeeSocialDeduction') ? 'font-weight: 600;' : ''}">
								<td style="vertical-align: middle; padding-bottom: 16px!important;">${row.description}</td>
								<td style="vertical-align: middle; padding-bottom: 16px!important;">${row.currency || ''}</td>
								<td style="vertical-align: middle; padding-bottom: 16px!important;">${row.value || ''}</td>
								${hasHours ? `<td style="vertical-align: middle; padding-bottom: 16px!important;">${row.fullValue || ''}</td>` : ''}
							</tr>
						`).join('')}
					</tbody>
				</table>
			</div>
			<div style="height: 100px; max-width: 28rem; margin: 20px auto 0 auto; border-top: 1px solid #333333; padding-top: 15px; text-align: center; font-size: 14px; color: #666; word-break: break-word; overflow: visible;">
    			PayFlow GmbH | Bahnhofplatz | 6300 Zug | kontakt@payflow.ch
			</div>
		`;

		html2pdf()
			.set({
				margin: [0.3, 1, 0.2, 1],
				filename: 'salary-calc.pdf',
				image: { type: 'jpeg', quality: 0.98 },
				html2canvas: { 
					scale: 2,
					useCORS: true,
					allowTaint: true
				},
				jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
			})
			.from(tempDiv)
			.save();
	};

	return (
		<PageShell
			width="narrow"
			embed={isPublicCalculator}
			surface={isPublicCalculator ? 'white' : 'app'}
			bottomNav={!isPublicCalculator}
			title={
				!isPublicCalculator ? t('salaryCalc.title') : undefined
			}
		>
			<Form
				content={
				<form onSubmit={handleSubmit} className="flex flex-col gap-4">
					<div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
						<Dropdown
							label={t('salaryCalc.typeOfSimulation')}
							name="simulationType"
							value={formData.simulationType}
							options={simulationOptions.map((option) => ({
								label: t(option.labelKey),
								value: option.value,
							}))}
							onChange={handleChange}
							fullWidth
						/>
						<Dropdown
							label={t('salaryCalc.serviceModel')}
							name="serviceFee"
							value={formData.serviceFee}
							options={serviceFeeOptions.map((option) => ({
								label: t(option.labelKey),
								value: option.value,
							}))}
							onChange={handleChange}
							fullWidth
						/>
					</div>
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
						<Dropdown
							label={t('salaryCalc.typeOfContract')}
							name="invoiceType"
							value={formData.invoiceType}
							options={contractOptions.map((option) => ({
								label: t(option.labelKey),
								value: option.value,
							}))}
							onChange={handleInvoiceTypeChange}
							fullWidth
						/>
						<Dropdown
							label={t('salaryCalc.age')}
							name="age"
							value={formData.age}
							options={ageOptions.map((option) => ({
								label: `${option.labelKey == 'Select' ? t('salaryCalc.select') : option.labelKey}`,
								value: option.value,
							}))}
							onChange={handleChange}
							fullWidth
						/>
					</div>
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
						<InputField
							label={t('createNewInvoice.hours')}
							name="hours"
							value={formData.hours}
							onChange={handleChange}
							placeholder="Hours"
							type="number"
							fullWidth
							disable={formData.invoiceType !== 'Flat Rate'}
						/>
						<InputField
							label={getLabelGrossAmountByType()}
							name="grossAmount"
							value={formData.grossAmount}
							onChange={handleChange}
							placeholder={getLabelGrossAmountByType()}
							dropdown={{
								name: 'currency',
								value: formData.currency,
								onChange: handleChange,
								options: [
									{ label: t('createNewInvoice.usd'), value: 'USD' },
									{ label: t('createNewInvoice.eur'), value: 'EUR' },
									{ label: t('createNewInvoice.chf'), value: 'CHF' },
								],
							}}
							fullWidth
						/>
					</div>
					<div ref={tableRef} className='mt-3'>
							{showTable && (
								<div className="overflow-x-auto -mx-[4px] px-[4px]">
									<DataTable
										size='small'
										value={rows} 
										tableStyle={{ margin: '0 auto', minWidth: '20rem', maxWidth: '35rem', width: '100%', borderCollapse: 'collapse' }} 
										className="custom-data-table"
										rowClassName={(data: any) => {
											if (data.highlight) {
												return 'highlight-row';
											}
											if (data.noBorder) {
												return 'no-border-row';
											}
											if (data.hidden) {
												return 'hidden-row';
											}
										}}
									>
										<Column 
											field="description" 
											header={t('salaryCalc.totalCosts')}
											headerStyle={{ backgroundColor: '#2f435f', color: '#ffffff' }}
											body={(data: any) => {
												if (data.id === 10) {
													console.log('data.value', data.value);
													if (data.value <= 0) {
														data.value = (
															<div style={{ display: 'flex', alignItems: 'center' }}>
																<span>{data.value}</span>
																<Tooltip target=".gross-amount-info" />
																<i className="gross-amount-info pi pi-info-circle p-text-secondary p-overlay-badge ml-1"
																	data-pr-tooltip={t('salaryCalc.grossAmountTooltip') || ''}
																	data-pr-position="right"
																	data-pr-at="right+5 top"
																	data-pr-my="left center-2"
																	style={{ fontSize: '1rem', cursor: 'pointer' }}>
																</i>
															</div>
														);
													}
												}
												if (data.id === 17) {
													if (data.value <= 0) {
														data.value = (
															<div style={{ display: 'flex', alignItems: 'center' }}>
																<span>{data.value}</span>
																<Tooltip target=".gross-amount-info" />
																<i className="gross-amount-info pi pi-info-circle p-text-secondary p-overlay-badge ml-1"
																	data-pr-tooltip={t('salaryCalc.grossAmountTooltip') || ''}
																	data-pr-position="right"
																	data-pr-at="right+5 top"
																	data-pr-my="left center-2"
																	style={{ fontSize: '1rem', cursor: 'pointer' }}>
																</i>
															</div>
														);
													}
												}
												if (data.id === 3) {
													return (
														<div style={{ display: 'flex', alignItems: 'center' }}>
															<span>{data.description}</span>
															<span
																style={{ marginLeft: '8px', cursor: 'pointer' }}
																onClick={() => {
																	const updatedRows = rows.map((row: any) => {
																		if (row.id === 3) {
																			return { ...row, value: !showEmployerSocialBenefits ? totalEmployerSocialBenefits : '', currency: !showEmployerSocialBenefits ? formData.currency : '', fullValue: !showEmployerSocialBenefits ? totalFullEmployerSocialBenefits : '' };
																		}
																		if (row.id === 4 || row.id === 5 || row.id === 6 || row.id === 7 || row.id === 8 || row.id === 9 || row.id === 91) {	
																			return { ...row, hidden: !row.hidden };
																		}
																		return row;
																	});
																	setRows(updatedRows);
																	setShowEmployerSocialBenefits(!showEmployerSocialBenefits);
																}}
															>
																{showEmployerSocialBenefits ? (
																	<i className="pi pi-caret-down"></i>
																) : (
																	<i className="pi pi-caret-right"></i>
																)}
															</span>
														</div>
													);
												} else if (data.id === 12) {
													return (
														<div style={{ display: 'flex', alignItems: 'center' }}>
															<span>{data.description}</span>
															<span
																style={{ marginLeft: '8px', cursor: 'pointer' }}
																onClick={() => {
																	const updatedRows = rows.map((row: any) => {
																		if (row.id === 12) {
																			return { ...row, value: !showEmployeeSocialBenefits ? totalEmployeeSocialBenefits : '', currency: !showEmployeeSocialBenefits ? formData.currency : '', fullValue: !showEmployeeSocialBenefits ? totalFullEmployeeSocialBenefits : '' };
																		}
																		if (row.id === 13 || row.id === 14 || row.id === 15 || row.id === 16 || row.id === 161) {
																			return { ...row, hidden: !row.hidden };
																		}
																		return row;
																	});
																	setRows(updatedRows);
																	setShowEmployeeSocialBenefits(!showEmployeeSocialBenefits);
																}}
															>
																{showEmployeeSocialBenefits ? (
																	<i className="pi pi-caret-down"></i>
																) : (
																	<i className="pi pi-caret-right"></i>
																)}
															</span>
														</div>
													);
												}
												return data.description;
											}}
										></Column>
										<Column field="currency" header="" headerStyle={{ backgroundColor: '#2f435f', color: '#ffffff' }}></Column>
										<Column
											field="value"
											alignHeader={'center'}
											header={hasHours ? '1h' : ''}
											headerStyle={{ backgroundColor: '#2f435f', color: '#ffffff' }}
											bodyStyle={{ textAlign: 'center' }}>
										</Column>
										{hasHours && (
											<Column
												field="fullValue"
												header={formData.hours+'h'}
												alignHeader={'center'}
												headerStyle={{ backgroundColor: '#2f435f', color: '#ffffff' }}
												bodyStyle={{ textAlign: 'center' }}
											></Column>
										)}
									</DataTable>
									<style>
										{`
											.custom-data-table .p-datatable-tbody > tr {
												border-bottom: 1px solid #d3d3d3;
												height: 40px!important;
											}
											.custom-data-table .highlight-row {
												background-color: #2f435f;
												color: #ffffff;
											}
											.custom-data-table .no-border-row {
												border: none!important;
											}
											.custom-data-table .hidden-row {
												display: none;
											}
											@media (max-width: 640px) {
												.custom-data-table {
													font-size: 13px;
												}
												.custom-data-table th,
												.custom-data-table td {
													padding: 6px 8px !important;
												}
											}
											.hidden-pdf-only {
												display: none;
											}
											@media print {
												.hidden-pdf-only {
													display: flex !important;
												}
											}
										`}
									</style>
								</div>
							)}
					</div>
					<div className={`flex flex-col-reverse sm:flex-row gap-4 mt-6 ${isPublicCalculator ? 'sm:justify-end' : 'sm:justify-between'}`}>
						{!isPublicCalculator && (
						<Button
							label={t('addNewClient.cancel')}
							onClick={handleCancel}
							color="#828E9F"
							fullWidth
						/>
						)}
						<div className='flex flex-col-reverse sm:flex-row gap-[12px] sm:items-center'>
							{showTable && (
								<Button
									label={t('salaryCalc.downloadPdf')}
									icon={<i className="pi pi-download" />}
									onClick={() => generatePDF()}
									color="#50b154"
									fullWidth
								/>
							)}
							<Button
								label={t('salaryCalc.calcBtn')}
								type="submit"
								fullWidth
							/>
						</div>
					</div>
				</form>
				}
			/>
		</PageShell>
	);
};
